import React from "react";
import { I18n } from "react-redux-i18n";
import cx from "classnames";

import { Loading } from "@common/components/loading";
import { getTestAttributes } from "@common/utils/tests";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";

import { FieldColumnKey, FieldsProps } from "./fields.types";
import { Field } from "./components/field";

import "./fields.styles.scss";

export const Fields: React.FC<FieldsProps> = (props) => {
  const { fields, loading, className, moduleName, selection, itemUniqProperty, onDrag } = props;

  const tableColumnsConfig: Array<{ label: string; field: FieldColumnKey }> = React.useMemo(
    () => [
      {
        label: I18n.t("Format Name"),
        field: FieldColumnKey.name,
      },
    ],
    []
  );

  const handleRowClickUp = React.useCallback(
    ({ item, event }: { item: FieldPrimaryListDto; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as FieldPrimaryListDto[]);
      let newSelectedItems = [item];

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        if (selection?.onChange) {
          selection.onChange(newSelectedItems);
        }
      }
    },
    [selection]
  );

  const handleRowClickDown = React.useCallback(
    ({ item, event }: { item: FieldPrimaryListDto; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as FieldPrimaryListDto[]);
      let newSelectedItems = [item];
      const isAlreadySelected = alreadySelectedItems.some((x) => x[itemUniqProperty] === item[itemUniqProperty]);

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        return;
      } else if ((event.ctrlKey || event.shiftKey) && selection?.type === "multiple") {
        newSelectedItems = isAlreadySelected
          ? alreadySelectedItems.filter((x) => x[itemUniqProperty] !== item[itemUniqProperty])
          : alreadySelectedItems.concat(item);
      } else if (alreadySelectedItems.length === 1 && isAlreadySelected) {
        return;
      }

      if (selection?.onChange) {
        selection.onChange(newSelectedItems);
      }
    },
    [selection, itemUniqProperty]
  );

  const isRowSelected = React.useCallback(
    (item: FieldPrimaryListDto) => {
      return selection?.items.some((x) => x[itemUniqProperty] === item[itemUniqProperty]) ?? false;
    },
    [selection?.items, itemUniqProperty]
  );

  return (
    <div className={cx("QueryCreatingFolderFields", className)}>
      {loading && <Loading size={20} margin="20px" />}
      {!loading && !fields.length && (
        <div className="QueryCreatingFolderFields__no-data-message">{I18n.t("No data")}</div>
      )}
      {!loading && fields.length > 0 && (
        <div className="QueryCreatingFolderFields__list">
          {fields.map((field, rowIndex) => (
            <Field
              key={field.id}
              field={field}
              onMouseUp={handleRowClickUp}
              onMouseDown={handleRowClickDown}
              tableColumnsConfig={tableColumnsConfig}
              isSelected={isRowSelected(field)}
              onDrag={onDrag}
              getId={{
                rowId: getTestAttributes({ id: `ss-format-grid-tbody-row${rowIndex}` }).id,
                rowColumnId: getTestAttributes(moduleName, `query-creating-formats-${rowIndex}`).id,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
